import React, { useState, useEffect } from 'react';
import { teamAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Users, UserPlus, Crown, Shield, Globe, MapPin,
  Trash2, X, Check, Mail, Clock, ChevronDown, Edit2, Save
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const TeamPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [maxTeam, setMaxTeam] = useState(0);
  const [planName, setPlanName] = useState('free');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  useEffect(() => { fetchTeam(); }, []);

  const fetchTeam = async () => {
    try {
      const res = await teamAPI.getMembers();
      setMembers(res.data.members || []);
      setPendingInvites(res.data.pending_invitations || []);
      setMaxTeam(res.data.max_team_members || 0);
      setPlanName(res.data.plan_name || 'free');
      setLocations(res.data.locations || []);
    } catch { toast.error('Failed to load team data'); }
    finally { setLoading(false); }
  };

  const handleRemoveMember = async (memberId, name) => {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    try {
      await teamAPI.removeMember(memberId);
      toast.success('Team member removed');
      fetchTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove member'); }
  };

  const handleCancelInvite = async (invitationId) => {
    try {
      await teamAPI.cancelInvitation(invitationId);
      toast.success('Invitation cancelled');
      fetchTeam();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel invitation'); }
  };

  const totalUsed = members.length + pendingInvites.length;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#1A73E8] to-[#0D47A1] rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Team Management</h1>
            <p className="text-[#5F6368]">Invite members and manage access to your locations</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-[#F1F3F4] rounded-lg px-3 py-1.5 text-sm">
            <Crown className="w-4 h-4 text-[#F9AB00]" />
            <span className="text-[#5F6368]">
              <strong className="text-[#202124] capitalize">{planName}</strong> — {totalUsed}/{maxTeam} members
            </span>
          </div>
          {maxTeam > 0 && totalUsed < maxTeam && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn-google flex items-center gap-2"
              data-testid="invite-member-btn"
            >
              <UserPlus className="w-4 h-4" /> Invite Member
            </button>
          )}
        </div>
      </div>

      {/* Free plan upgrade notice */}
      {maxTeam === 0 && (
        <div className="dashboard-card text-center py-8" data-testid="team-upgrade-notice">
          <Users className="w-12 h-12 text-[#DADCE0] mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-[#202124] mb-1" style={{ fontFamily: 'Outfit' }}>Team Collaboration</h3>
          <p className="text-sm text-[#5F6368] mb-4 max-w-md mx-auto">
            Invite team members to help manage your Google Business Profile. Upgrade to Basic (2 members) or Pro (5 members) to get started.
          </p>
          <Link to="/pricing" className="btn-google inline-flex items-center gap-2" data-testid="team-upgrade-btn">
            <Crown className="w-4 h-4" /> Upgrade Plan
          </Link>
        </div>
      )}

      {/* Active Members */}
      {maxTeam > 0 && (
        <div className="dashboard-card" data-testid="team-members-section">
          <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
            <Shield className="w-4 h-4 inline mr-2 text-[#1A73E8]" />Active Members
          </h3>
          {members.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-[#5F6368]">No team members yet. Invite someone to get started!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {members.map((m) => (
                <MemberCard
                  key={m.id}
                  member={m}
                  locations={locations}
                  onRemove={() => handleRemoveMember(m.id, m.member_name)}
                  onEdit={() => setEditingMember(m)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="dashboard-card" data-testid="pending-invitations-section">
          <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
            <Clock className="w-4 h-4 inline mr-2 text-[#F9AB00]" />Pending Invitations
          </h3>
          <div className="space-y-3">
            {pendingInvites.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between p-3 bg-[#FEF7E0] rounded-lg border border-[#FDE293]" data-testid={`invite-${inv.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#F9AB00]/20 rounded-full flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#F9AB00]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#202124]">{inv.invitee_email}</p>
                    <p className="text-xs text-[#5F6368]">
                      {inv.access_type === 'global' ? 'All Locations' : `${inv.location_ids?.length || 0} location(s)`} · Expires {new Date(inv.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleCancelInvite(inv.id)}
                  className="p-2 text-[#D93025] hover:bg-[#FCE8E6] rounded-lg transition-colors"
                  title="Cancel invitation"
                  data-testid={`cancel-invite-${inv.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          locations={locations}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => { setShowInviteModal(false); fetchTeam(); }}
        />
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          locations={locations}
          onClose={() => setEditingMember(null)}
          onSuccess={() => { setEditingMember(null); fetchTeam(); }}
        />
      )}
    </div>
  );
};

// --- Member Card Component ---
const MemberCard = ({ member, locations, onRemove, onEdit }) => {
  const accessLabel = member.access_type === 'global'
    ? 'All Locations'
    : `${member.location_ids?.length || 0} location(s)`;

  const assignedNames = member.access_type === 'per_location'
    ? locations.filter(l => member.location_ids?.includes(l.id)).map(l => l.name)
    : [];

  return (
    <div className="flex items-center justify-between p-3 bg-[#F8F9FA] rounded-lg border border-[#DADCE0]" data-testid={`member-${member.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-[#E8F0FE] rounded-full flex items-center justify-center">
          <Shield className="w-4 h-4 text-[#1A73E8]" />
        </div>
        <div>
          <p className="text-sm font-medium text-[#202124]">{member.member_name || member.member_email}</p>
          <div className="flex items-center gap-2 text-xs text-[#5F6368]">
            <span className="capitalize">{member.role}</span>
            <span>·</span>
            {member.access_type === 'global' ? (
              <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {accessLabel}</span>
            ) : (
              <span className="flex items-center gap-1" title={assignedNames.join(', ')}><MapPin className="w-3 h-3" /> {accessLabel}</span>
            )}
            <span>·</span>
            <span>Joined {new Date(member.joined_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onEdit} className="p-2 text-[#5F6368] hover:bg-[#E8F0FE] rounded-lg transition-colors" title="Edit access" data-testid={`edit-member-${member.id}`}>
          <Edit2 className="w-4 h-4" />
        </button>
        <button onClick={onRemove} className="p-2 text-[#D93025] hover:bg-[#FCE8E6] rounded-lg transition-colors" title="Remove member" data-testid={`remove-member-${member.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// --- Invite Modal ---
const InviteModal = ({ locations, onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [accessType, setAccessType] = useState('global');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [sending, setSending] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const toggleLocation = (locId) => {
    setSelectedLocations(prev =>
      prev.includes(locId) ? prev.filter(id => id !== locId) : [...prev, locId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (accessType === 'per_location' && selectedLocations.length === 0) {
      toast.error('Please select at least one location');
      return;
    }
    setSending(true);
    try {
      const res = await teamAPI.invite({
        email,
        role: 'manager',
        access_type: accessType,
        location_ids: accessType === 'per_location' ? selectedLocations : [],
      });
      toast.success(res.data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="invite-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="invite-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#DADCE0]">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            <UserPlus className="w-5 h-5 inline mr-2 text-[#1A73E8]" />Invite Team Member
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F1F3F4] flex items-center justify-center" data-testid="invite-modal-close">
            <X className="w-4 h-4 text-[#5F6368]" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="member@example.com"
              required
              className="input-google"
              data-testid="invite-email-input"
            />
          </div>

          {/* Access Type */}
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-2">Location Access</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAccessType('global')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  accessType === 'global' ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#DADCE0] hover:border-[#5F6368]'
                }`}
                data-testid="access-global-btn"
              >
                <Globe className="w-5 h-5 text-[#1A73E8] mb-1" />
                <p className="text-sm font-medium text-[#202124]">All Locations</p>
                <p className="text-xs text-[#5F6368]">Access everything</p>
              </button>
              <button
                type="button"
                onClick={() => setAccessType('per_location')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  accessType === 'per_location' ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#DADCE0] hover:border-[#5F6368]'
                }`}
                data-testid="access-per-location-btn"
              >
                <MapPin className="w-5 h-5 text-[#1A73E8] mb-1" />
                <p className="text-sm font-medium text-[#202124]">Specific</p>
                <p className="text-xs text-[#5F6368]">Choose locations</p>
              </button>
            </div>
          </div>

          {/* Location Selection */}
          {accessType === 'per_location' && (
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-1">Select Locations</label>
              {locations.length === 0 ? (
                <p className="text-sm text-[#5F6368] bg-[#F1F3F4] p-3 rounded-lg">No activated locations yet. Connect and activate locations first.</p>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                    className="input-google w-full flex items-center justify-between cursor-pointer"
                    data-testid="location-dropdown-btn"
                  >
                    <span className="text-sm text-[#5F6368]">
                      {selectedLocations.length === 0 ? 'Select locations...' : `${selectedLocations.length} selected`}
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#5F6368]" />
                  </button>
                  {showLocationDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#DADCE0] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {locations.map(loc => (
                        <button
                          key={loc.id}
                          type="button"
                          onClick={() => toggleLocation(loc.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F1F3F4] text-left text-sm"
                          data-testid={`location-option-${loc.id}`}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            selectedLocations.includes(loc.id) ? 'bg-[#1A73E8] border-[#1A73E8]' : 'border-[#DADCE0]'
                          }`}>
                            {selectedLocations.includes(loc.id) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <MapPin className="w-3 h-3 text-[#5F6368]" />
                          <span className="text-[#202124]">{loc.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="bg-[#E8F0FE] p-3 rounded-lg text-xs text-[#1A73E8]">
            <strong>Manager access:</strong> Create posts, view analytics, manage templates. Cannot access billing, team settings, or account settings.
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={sending}
            className="w-full btn-google flex items-center justify-center gap-2"
            data-testid="send-invite-btn"
          >
            {sending ? (
              <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Sending...</>
            ) : (
              <><Mail className="w-4 h-4" /> Send Invitation</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// --- Edit Member Modal ---
const EditMemberModal = ({ member, locations, onClose, onSuccess }) => {
  const [accessType, setAccessType] = useState(member.access_type || 'global');
  const [selectedLocations, setSelectedLocations] = useState(member.location_ids || []);
  const [saving, setSaving] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const toggleLocation = (locId) => {
    setSelectedLocations(prev =>
      prev.includes(locId) ? prev.filter(id => id !== locId) : [...prev, locId]
    );
  };

  const handleSave = async () => {
    if (accessType === 'per_location' && selectedLocations.length === 0) {
      toast.error('Please select at least one location');
      return;
    }
    setSaving(true);
    try {
      await teamAPI.updateMember(member.id, {
        role: 'manager',
        access_type: accessType,
        location_ids: accessType === 'per_location' ? selectedLocations : [],
      });
      toast.success('Member access updated');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update member');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="edit-member-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="edit-member-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#DADCE0]">
          <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            <Edit2 className="w-5 h-5 inline mr-2 text-[#1A73E8]" />Edit Access — {member.member_name || member.member_email}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F1F3F4] flex items-center justify-center" data-testid="edit-modal-close">
            <X className="w-4 h-4 text-[#5F6368]" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#202124] mb-2">Location Access</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAccessType('global')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  accessType === 'global' ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#DADCE0] hover:border-[#5F6368]'
                }`}
                data-testid="edit-access-global-btn"
              >
                <Globe className="w-5 h-5 text-[#1A73E8] mb-1" />
                <p className="text-sm font-medium text-[#202124]">All Locations</p>
              </button>
              <button
                type="button"
                onClick={() => setAccessType('per_location')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  accessType === 'per_location' ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#DADCE0] hover:border-[#5F6368]'
                }`}
                data-testid="edit-access-per-location-btn"
              >
                <MapPin className="w-5 h-5 text-[#1A73E8] mb-1" />
                <p className="text-sm font-medium text-[#202124]">Specific</p>
              </button>
            </div>
          </div>

          {accessType === 'per_location' && (
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-1">Select Locations</label>
              {locations.length === 0 ? (
                <p className="text-sm text-[#5F6368] bg-[#F1F3F4] p-3 rounded-lg">No activated locations.</p>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                    className="input-google w-full flex items-center justify-between cursor-pointer"
                    data-testid="edit-location-dropdown-btn"
                  >
                    <span className="text-sm text-[#5F6368]">
                      {selectedLocations.length === 0 ? 'Select locations...' : `${selectedLocations.length} selected`}
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#5F6368]" />
                  </button>
                  {showLocationDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#DADCE0] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {locations.map(loc => (
                        <button
                          key={loc.id}
                          type="button"
                          onClick={() => toggleLocation(loc.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F1F3F4] text-left text-sm"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            selectedLocations.includes(loc.id) ? 'bg-[#1A73E8] border-[#1A73E8]' : 'border-[#DADCE0]'
                          }`}>
                            {selectedLocations.includes(loc.id) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <MapPin className="w-3 h-3 text-[#5F6368]" />
                          <span className="text-[#202124]">{loc.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 bg-[#F1F3F4] text-[#5F6368] rounded-lg text-sm font-medium hover:bg-[#E8EAED] transition-colors" data-testid="edit-cancel-btn">Cancel</button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 btn-google flex items-center justify-center gap-2"
              data-testid="edit-save-btn"
            >
              {saving ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Saving...</> : <><Save className="w-4 h-4" /> Save Changes</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
